import { useState } from 'react';
import { Camera, AlertTriangle, Minimize2, Loader2, VideoOff } from 'lucide-react';
import { useLiveProctoring } from './useLiveProctoring';

const LiveProctoringMonitor = ({ sessionId, isActive, preCalibratedBaselines = null }) => {
  const {
    videoRef,
    stream,
    cameraError,
    isCalibrated,
    calibrationProgress,
    activeWarning,
    modelsLoading,
    modelError
  } = useLiveProctoring({ sessionId, isActive, preCalibratedBaselines });

  const [isMinimized, setIsMinimized] = useState(false);

  if (modelError) {
    return (
      <div className="fixed bottom-4 right-4 z-50 p-4 w-60 rounded-xl bg-error/15 text-error border border-error/20 backdrop-blur-md text-xs shadow-2xl flex items-start gap-2.5">
        <VideoOff className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-bold">Proctoring Error</h4>
          <p>{modelError}</p>
        </div>
      </div>
    );
  }

  if (cameraError) {
    return (
      <div className="fixed bottom-4 right-4 z-50 p-4 w-60 rounded-xl bg-error/15 text-error border border-error/20 backdrop-blur-md text-xs shadow-2xl flex items-start gap-2.5">
        <VideoOff className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-bold">Camera Blocked</h4>
          <p>{cameraError}</p>
        </div>
      </div>
    );
  }

  if (modelsLoading) {
    return (
      <div className="fixed bottom-4 right-4 z-50 p-4 w-60 rounded-xl bg-base-100/80 border border-base-300 backdrop-blur-md text-xs shadow-2xl flex items-center gap-3">
        <Loader2 className="w-5 h-5 animate-spin text-primary" />
        <div>
          <h4 className="font-bold">Initializing Proctoring</h4>
          <p className="text-gray-500 text-[10px]">Loading AI vision models...</p>
        </div>
      </div>
    );
  }

  // Collapsed Badge State
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-4 right-4 z-50 p-3.5 rounded-full bg-base-100 shadow-2xl hover:scale-105 transition-all duration-200 border border-base-300 flex items-center justify-center group"
        title="Show Proctoring Camera"
      >
        <div className="relative">
          <Camera className="w-6 h-6 text-primary" />
          <span className={`absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full border-2 border-white ${
            activeWarning ? 'bg-error animate-ping' : 'bg-success animate-pulse'
          }`} />
          {/* Overlay tiny indicator */}
          <span className={`absolute -top-1.5 -right-1.5 w-3.5 h-3.5 rounded-full border-2 border-white ${
            activeWarning ? 'bg-error' : 'bg-success'
          }`} />
        </div>
      </button>
    );
  }

  return (
    <div className={`fixed bottom-4 right-4 z-50 w-52 md:w-56 overflow-hidden rounded-2xl border transition-all duration-300 shadow-2xl backdrop-blur-md ${
      activeWarning 
        ? 'border-error/50 bg-error/10 animate-shake-widget' 
        : 'border-base-300 bg-base-100/80'
    }`}>
      {/* Header bar */}
      <div className="px-3.5 py-2 flex items-center justify-between border-b border-base-300/40 text-[11px] font-bold text-gray-500 select-none bg-base-200/40">
        <span className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${
            !isCalibrated ? 'bg-warning animate-pulse' : activeWarning ? 'bg-error animate-ping' : 'bg-success animate-pulse'
          }`} />
          {!isCalibrated ? 'Calibrating...' : activeWarning ? 'Suspicious!' : 'Proctoring Active'}
        </span>
        <button 
          onClick={() => setIsMinimized(true)} 
          className="hover:bg-base-300 p-0.5 rounded transition-all text-gray-400 hover:text-base-content"
          title="Minimize to badge"
        >
          <Minimize2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Video / Guide body */}
      <div className="relative aspect-[4/3] bg-black">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="w-full h-full object-cover scale-x-[-1]"
        />

        {/* Warning Indicator Outline Overlay */}
        {activeWarning && (
          <div className="absolute inset-0 border-4 border-error animate-pulse pointer-events-none" />
        )}

        {/* Calibration Progress overlay */}
        {!isCalibrated && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center p-3 text-center text-white">
            <Loader2 className="w-6 h-6 animate-spin text-primary mb-2" />
            <p className="text-[11px] font-semibold leading-tight">Calibration Camera Setup</p>
            <p className="text-[9px] text-gray-400 mt-1">Please look straight at the screen</p>
            <div className="w-20 bg-gray-700 h-1.5 rounded-full mt-2.5 overflow-hidden">
              <div 
                className="bg-primary h-full transition-all duration-200" 
                style={{ width: `${calibrationProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Warning Text Overlay */}
        {isCalibrated && activeWarning && (
          <div className="absolute inset-x-2 bottom-2 p-1.5 bg-black/85 border border-error/20 rounded-lg text-center text-error flex items-center justify-center gap-1.5 shadow-xl animate-bounce">
            <AlertTriangle className="w-3.5 h-3.5 animate-pulse" />
            <span className="text-[9px] font-bold tracking-wide uppercase">
              {activeWarning === 'frame_exit' && 'Face out of frame'}
              {activeWarning === 'multiple_faces' && 'Multi-face detected'}
              {activeWarning === 'head_turn' && 'Look at screen'}
              {activeWarning === 'eye_wandering' && 'Eyes on exam'}
            </span>
          </div>
        )}
      </div>

      <style>{`
        .animate-shake-widget {
          animation: shakeWidget 0.4s ease-in-out;
        }
        @keyframes shakeWidget {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-4px); }
          40%, 80% { transform: translateX(4px); }
        }
      `}</style>
    </div>
  );
};

export default LiveProctoringMonitor;
